import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Input, Button, Select, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import * as SolidIcons from '@heroicons/react/solid';
import SpecDrawer from '../SpecDrawer/SpecDrawer';
import SpecBottomSheet from '../SpecBottomSheet/SpecBottomSheet';
import SpecModal from '../SpecModal/SpecModal';
import SpecAlert from '../SpecAlert/SpecAlert';
import Tag from '../Tag/Tag';
import { DefaultAvatar, ChevronRightIcon } from '../icons';
import {
  list_show_contributors,
  list_available_contributors,
  add_show_contributor,
  remove_show_contributor,
  ShowContributorRecord,
  AvailableFan,
} from '../../api';
import './ManageDrawer.css';

interface Contributor {
  id: string;
  name: string;
  email?: string;
  avatarUrl?: string;
  pending?: boolean;
  user_id?: string;
}

export interface ShowContributorsDrawerProps {
  open: boolean;
  onClose: () => void;
  bandName: string;
  bandId?: string;
}

// Convert API ShowContributorRecord to display Contributor
const showContributorToContributor = (sc: ShowContributorRecord): Contributor => ({
  id: sc.id,
  user_id: sc.user_id,
  name: sc.user_name || sc.user_email || 'Unknown User',
  email: sc.user_email,
});

export const ShowContributorsDrawer: React.FC<ShowContributorsDrawerProps> = ({
  open,
  onClose,
  bandName,
  bandId,
}) => {
  const queryClient = useQueryClient();
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [availableFans, setAvailableFans] = useState<AvailableFan[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Add contributor state
  const [addMode, setAddMode] = useState(false);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  // Modal state
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removePasscode, setRemovePasscode] = useState('');

  // Toast state
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastKeyRef = useRef(0);

  const showToast = useCallback((message: string) => {
    toastKeyRef.current += 1;
    setToastMessage(message);
  }, []);

  // Load contributors and available fans when drawer opens
  useEffect(() => {
    if (open && bandId) {
      loadContributors();
      loadAvailableFans();
    }
  }, [open, bandId]);

  const loadContributors = async () => {
    if (!bandId) return;
    setLoading(true);
    try {
      const data = await list_show_contributors(bandId);
      // Deduplicate by user_id in case of multiple records
      const seen = new Set<string>();
      const deduped = data.filter(sc => {
        if (!sc.user_id || seen.has(sc.user_id)) return false;
        seen.add(sc.user_id);
        return true;
      });
      setContributors(deduped.map(showContributorToContributor));
    } catch (error) {
      console.error('Failed to load contributors:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableFans = async () => {
    if (!bandId) return;
    try {
      const data = await list_available_contributors(bandId);
      setAvailableFans(data);
    } catch (error) {
      console.error('Failed to load available fans:', error);
    }
  };

  const selectedContributor = contributors.find((c) => c.id === selectedId) || null;
  const sortedContributors = [...contributors].sort((a, b) => {
    if (a.pending && !b.pending) return 1;
    if (!a.pending && b.pending) return -1;
    return 0;
  });

  const handleSelectContributor = (id: string) => {
    setSelectedId(id);
  };

  const closeDetailSheet = () => {
    setSelectedId(null);
  };

  const handleAddContributor = async () => {
    if (!bandId || !selectedUid) return;

    setAdding(true);
    try {
      await add_show_contributor(bandId, selectedUid);
      await loadContributors();
      await loadAvailableFans();
      // Invalidate React Query cache so BandDetail page updates
      queryClient.invalidateQueries({ queryKey: ['band', bandId, 'show-contributors'] });
      setSelectedUid(null);
      setAddMode(false);
      showToast('Contributor Added');
    } catch (error: any) {
      message.error(error.message || 'Failed to add contributor');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveContributor = async () => {
    if (!selectedContributor || !bandId || !selectedContributor.user_id) return;

    try {
      await remove_show_contributor(bandId, selectedContributor.user_id);
      await loadContributors();
      await loadAvailableFans();
      // Invalidate React Query cache so BandDetail page updates
      queryClient.invalidateQueries({ queryKey: ['band', bandId, 'show-contributors'] });
      setRemoveModalOpen(false);
      setRemovePasscode('');
      closeDetailSheet();
      showToast('Contributor Removed');
    } catch (error: any) {
      message.error(error.message || 'Failed to remove contributor');
    }
  };

  const handleClose = () => {
    setSelectedId(null);
    setAddMode(false);
    setSelectedUid(null);
    onClose();
  };

  const customTitle = (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <div
        style={{
          width: '40px',
          height: '4px',
          background: 'rgba(255, 255, 255, 0.3)',
          borderRadius: '2px',
          margin: '0 auto 24px',
          flexShrink: 0,
        }}
      />
      Show Contributors
      <p className="manage-drawer__subtitle" style={{ marginTop: 8 }}>Add or remove show contributors for band</p>
    </div>
  );

  return (
    <>
      <SpecDrawer
        title={customTitle}
        open={open}
        onClose={handleClose}
        size="large"
        swipeable={false}
        closable={false}
      >
        <div className="manage-drawer-content">
          {/* Add Contributor Section */}
          {bandId && (
            <div className="manage-drawer__invite">
              {!addMode ? (
                <Button
                  type="primary"
                  shape="round"
                  block
                  onClick={() => setAddMode(true)}
                  disabled={availableFans.length === 0}
                >
                  {availableFans.length === 0 ? 'No Fans Available' : 'Add Show Contributor'}
                </Button>
              ) : (
                <div className="manage-drawer__add-form">
                  <span className="manage-drawer__invite-label">Select Fan</span>
                  <Select
                    showSearch
                    placeholder="Search fans..."
                    value={selectedUid}
                    onChange={setSelectedUid}
                    style={{ width: '100%', marginBottom: 12 }}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={availableFans.map((fan) => ({
                      value: fan.uid,
                      label: fan.displayName || fan.email || fan.uid,
                    }))}
                  />
                  <div className="manage-drawer__invite-row">
                    <Button
                      type="primary"
                      shape="round"
                      onClick={handleAddContributor}
                      disabled={!selectedUid}
                      loading={adding}
                    >
                      Add
                    </Button>
                    <Button shape="round" onClick={() => { setAddMode(false); setSelectedUid(null); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contributors list */}
          <>
            <span className="manage-drawer__section-label">Contributors</span>
            {loading ? (
              <div className="manage-drawer__loading">Loading contributors...</div>
            ) : contributors.length === 0 ? (
              <div className="manage-drawer__empty">No contributors yet</div>
            ) : (
              <div className="manage-drawer__list">
                {sortedContributors.map((contributor) => (
                  <div
                    key={contributor.id}
                    className="manage-drawer__person"
                    onClick={() => handleSelectContributor(contributor.id)}
                  >
                    <div className="manage-drawer__person-avatar">
                      {contributor.avatarUrl ? (
                        <img src={contributor.avatarUrl} alt={contributor.name} />
                      ) : (
                        <DefaultAvatar size={40} />
                      )}
                    </div>
                    <div className="manage-drawer__person-info">
                      <span className="manage-drawer__person-name">{contributor.name}</span>
                      {contributor.email && (
                        <span className="manage-drawer__person-email">
                          {contributor.email}{contributor.pending && <i> (invite sent)</i>}
                        </span>
                      )}
                    </div>
                    <div className="manage-drawer__person-role">
                      <Tag>{contributor.pending ? 'Pending' : 'Contributor'}</Tag>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        </div>
      </SpecDrawer>

      {/* Contributor detail bottom sheet */}
      <SpecBottomSheet
        open={!!selectedContributor}
        onClose={closeDetailSheet}
      >
        {selectedContributor && (
          <>
            {/* Header */}
            <div className="manage-detail-sheet__header">
              <div className="manage-detail-sheet__avatar">
                {selectedContributor.avatarUrl ? (
                  <img src={selectedContributor.avatarUrl} alt={selectedContributor.name} />
                ) : (
                  <DefaultAvatar size={24} />
                )}
              </div>
              <div className="manage-detail-sheet__info">
                <span className="manage-detail-sheet__name">{selectedContributor.name}</span>
                {selectedContributor.email && (
                  <span className="manage-detail-sheet__status">
                    {selectedContributor.email}
                    {selectedContributor.pending && <i> (invite sent)</i>}
                  </span>
                )}
              </div>
            </div>

            {/* Role row */}
            <div className="action-sheet-items">
              <button className="action-sheet-item" style={{ cursor: 'default' }}>
                <span className="action-sheet-item-label">Show Contributor</span>
                <span className="manage-detail-sheet__role-check">
                  <SolidIcons.CheckCircleIcon />
                </span>
              </button>
            </div>

            <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', margin: '0 16px' }} />

            {/* Action rows */}
            <div className="action-sheet-items">
              <button
                className="action-sheet-item action-sheet-item-danger"
                onClick={() => setRemoveModalOpen(true)}
              >
                <span className="action-sheet-item-label">Remove</span>
                <ChevronRightIcon size="l" tone="darkGrey" />
              </button>
            </div>
          </>
        )}
      </SpecBottomSheet>

      {/* Remove Contributor Modal */}
      <SpecModal
        open={removeModalOpen}
        onCancel={() => {
          setRemoveModalOpen(false);
          setRemovePasscode('');
        }}
        title="Remove Show Contributor"
        footer={null}
        closable={false}
        centered
        width={340}
        zIndex={2200}
      >
        <p className="manage-drawer__modal-body">
          Type "removeshowcontributor" to remove {selectedContributor?.name || 'this contributor'} from {bandName}:
        </p>
        <div className="manage-drawer__modal-form">
          <span className="manage-drawer__modal-label">Remove Passcode</span>
          <Input
            placeholder="Enter passcode to remove"
            value={removePasscode}
            onChange={(e) => setRemovePasscode(e.target.value)}
          />
          <Button
            block
            size="large"
            shape="round"
            className="edit-profile-delete-btn"
            disabled={removePasscode !== 'removeshowcontributor'}
            onClick={handleRemoveContributor}
          >
            Remove Show Contributor
          </Button>
        </div>
      </SpecModal>

      <SpecAlert
        key={toastKeyRef.current}
        message={toastMessage}
        onDone={() => setToastMessage(null)}
      />
    </>
  );
};

export default ShowContributorsDrawer;
